import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toastError } from '../../toast';
import { useUserContext } from '../../contexts/UserContext';
import {
  bulkUpdateDocuments,
  bulkUpdateDocumentsByQuery,
  createBulkOperationJob,
  createTrashJob,
  createPurgeJob,
  getBulkOperationJobStatus,
  getTrashJobStatus,
  getPurgeJobStatus,
  searchDocuments,
} from '../../api/document';
import SearchComponent from './searchcomponent';
import DocumentList from '../documentlist';
import { BulkOperationJob, Document } from '../../types/document';
import { useCartContext } from '../../contexts/CartContext';
import { toast } from 'react-toastify';
import ConfirmBulkAction from '../documentlist/confirmBulkAction';

export type SortField = { field: string; ascending: boolean };
type SearchState = {
  query: string;
  currentPage?: number;
  documents?: Document[];
  searching: boolean;
  error?: string;
  totalCount: number;
  sort?: SortField;
  status?: string;
};

const defaultSort = undefined;
type Action =
  | { type: 'submit_search'; query: string; status?: string }
  | { type: 'reset_search' }
  | { type: 'update_page'; page: number }
  | { type: 'update_documents'; documents: Document[]; count: number }
  | { type: 'search_error'; message: string }
  | { type: 'update_sort'; sort: SortField }
  | { type: 'sticky_search'; prevState: SearchState };

const searchReducer = (state: SearchState, action: Action): SearchState => {
  switch (action.type) {
    case 'reset_search': {
      return {
        query: '',
        searching: false,
        totalCount: 0,
        sort: defaultSort,
      };
    }
    case 'update_page': {
      return {
        ...state,
        currentPage: action.page,
        searching: true,
      };
    }
    case 'update_sort': {
      return {
        ...state,
        currentPage: 1,
        sort: action.sort,
        searching: true,
      };
    }
    case 'update_documents': {
      return {
        ...state,
        documents: action.documents,
        totalCount: action.count,
        searching: false,
      };
    }
    case 'submit_search': {
      return {
        ...state,
        query: action.query,
        status: action.status,
        currentPage: 1,
        searching: true,
        totalCount: 0,
      };
    }
    case 'search_error': {
      return {
        ...state,
        query: '',
        documents: undefined,
        error: action.message,
        searching: false,
        totalCount: 0,
      };
    }
    case 'sticky_search': {
      return { ...action.prevState, searching: true };
    }
    default: {
      throw new Error('Unhandled action type');
    }
  }
};

const pageSize = 10;

const DocumentSearch = () => {
  const { currentAccount } = useUserContext();
  const { user } = useUserContext().profile;
  const [searchState, dispatch] = React.useReducer(searchReducer, {
    searching: false,
    query: '',
    documents: undefined,
    currentPage: undefined,
    error: undefined,
    totalCount: 0,
    sort: defaultSort,
  });
  const { query, currentPage, documents, searching, error, totalCount, sort, status } = searchState;
  const { addToCart } = useCartContext();
  const [showInactiveTypes, setShowInactiveTypes] = useState<boolean>(false);
  const searchStorageKey = `scribonline_documentsearch_${user.id}_${currentAccount.accountId}`;

  // Bulk selection state
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
  const [selectAllResultsMode, setSelectAllResultsMode] = useState<boolean>(false);
  const [excludedDocumentIds, setExcludedDocumentIds] = useState<string[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    headerText: string;
    bodyText: string;
    confirmButtonText: string;
    confirmButtonColor: string;
    warningText?: string;
    onConfirm: () => void;
  } | null>(null);

  // Async job state
  const [activeJob, setActiveJob] = useState<BulkOperationJob | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Threshold for using async jobs (1000 documents or more)
  const ASYNC_JOB_THRESHOLD = 1000;

  useEffect(() => {
    const htmlElement = document.documentElement;
    htmlElement.setAttribute('aria-label', 'Content');
  }, []);

  useEffect(() => {
    if (searching) searchDocumentsInternal();
  }, [searching]);

  useEffect(() => {
    const { query, currentPage, sort, documents } = searchState;
    if (query && documents && documents.length > 0) {
      localStorage.setItem(searchStorageKey, JSON.stringify({ query, currentPage, sort }));
    }
  }, [searchState]);

  useEffect(() => {
    const storageItem = localStorage.getItem(searchStorageKey);
    if (!storageItem) return;
    const prevState: SearchState = JSON.parse(storageItem);
    if (prevState.query) {
      dispatch({ type: 'sticky_search', prevState });
    }
  }, []);

  const sortString = useMemo(() => {
    if (!sort) return;
    return `${sort.field}:${sort.ascending ? 'asc' : 'desc'}`;
  }, [sort]);
  const searchDocumentsInternal = async () => {
    console.log('the query', query, currentPage, pageSize, sortString);
    const searchResults = await toastError(
      searchDocuments(currentAccount.accountId, query, undefined, currentPage, pageSize, sortString),
      'Error searching documents',
    );
    if (searchResults.count === 0) {
      dispatch({ type: 'search_error', message: 'No document results found.' });
      return;
    }
    dispatch({ type: 'update_documents', documents: searchResults.documents, count: searchResults.count });
  };

  const addAllToCart = async () => {
    const allResults = await searchDocuments(currentAccount.accountId, query, undefined, 1, totalCount);
    addToCart(allResults.documents.map((document: Document) => document.id));
  };

  const addResultsToCart = async () => {
    await toast.promise(addAllToCart(), {
      pending: 'Adding search results to cart.',
      success: 'Search results added to cart.',
      error: 'Unable to add search results to cart.',
    });
  };

  const resetSearch = useCallback(() => {
    dispatch({ type: 'reset_search' });
    setShowInactiveTypes(false);
    localStorage.removeItem(searchStorageKey);
  }, []);

  const submitSearch = useCallback((query: string, status?: string) => {
    dispatch({ type: 'submit_search', query, status });
  }, []);

  const updatePage = useCallback((newPage: number) => {
    dispatch({ type: 'update_page', page: newPage });
  }, []);

  const updateSort = useCallback((newSort: SortField) => {
    dispatch({ type: 'update_sort', sort: newSort });
  }, []);

  // Selection management
  const selectDocument = useCallback(
    (documentId: string, selected: boolean) => {
      if (selectAllResultsMode) {
        // In "select all results" mode, manage exclusions
        if (selected) {
          // Remove from exclusions
          setExcludedDocumentIds((prev) => prev.filter((id) => id !== documentId));
        } else {
          // Add to exclusions
          setExcludedDocumentIds((prev) => [...prev, documentId]);
        }
      } else {
        // In normal mode, manage selected documents
        setSelectedDocuments((prev) => {
          if (selected) {
            return [...prev, documentId];
          } else {
            return prev.filter((id) => id !== documentId);
          }
        });
      }
    },
    [selectAllResultsMode],
  );

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        // Only select non-permanent documents
        setSelectedDocuments(documents?.filter((doc) => !doc.permanent).map((doc) => doc.id) || []);
        setSelectAllResultsMode(false);
        setExcludedDocumentIds([]);
      } else {
        setSelectedDocuments([]);
        setSelectAllResultsMode(false);
        setExcludedDocumentIds([]);
      }
    },
    [documents],
  );

  const handleSelectAllResults = useCallback(() => {
    setSelectAllResultsMode(true);
    // Automatically exclude permanent documents from "select all results"
    const permanentDocIds = documents?.filter((doc) => doc.permanent).map((doc) => doc.id) || [];
    setExcludedDocumentIds(permanentDocIds);
    setSelectedDocuments([]);
  }, [documents]);

  // Compute effective selected count
  // In selectAllResultsMode, excludedDocumentIds already includes permanent docs
  const effectiveSelectedCount = useMemo(
    () => (selectAllResultsMode ? totalCount - excludedDocumentIds.length : selectedDocuments.length),
    [selectAllResultsMode, totalCount, excludedDocumentIds, selectedDocuments],
  );

  // Clear selections when search changes
  useEffect(() => {
    setSelectedDocuments([]);
    setSelectAllResultsMode(false);
    setExcludedDocumentIds([]);
  }, [query]);

  // Job polling
  const startJobPolling = useCallback(
    (jobId: string, operationType: 'trash' | 'purge' | 'other' = 'other') => {
      const interval = setInterval(async () => {
        try {
          let jobStatus;
          if (operationType === 'trash') {
            jobStatus = await getTrashJobStatus(currentAccount.accountId, jobId);
          } else if (operationType === 'purge') {
            jobStatus = await getPurgeJobStatus(currentAccount.accountId, jobId);
          } else {
            jobStatus = await getBulkOperationJobStatus(currentAccount.accountId, jobId);
          }
          setActiveJob(jobStatus);

          if (jobStatus.status === 'COMPLETED') {
            clearInterval(interval);
            setPollingInterval(null);
            toast.success(
              `✅ ${jobStatus.operation} completed! ${jobStatus.successfulDocuments.toLocaleString()} documents processed` +
                (jobStatus.excludedPermanentDocuments > 0 ? ` (${jobStatus.excludedPermanentDocuments} permanent docs excluded)` : ''),
            );
            setActiveJob(null);
            setSelectedDocuments([]);
            setSelectAllResultsMode(false);
            setExcludedDocumentIds([]);
            // Refresh search results - force re-search by resetting to not searching first
            dispatch({ type: 'update_page', page: searchState.currentPage || 1 });
          } else if (jobStatus.status === 'FAILED') {
            clearInterval(interval);
            setPollingInterval(null);
            toast.error(`❌ ${jobStatus.operation} failed: ${jobStatus.errorMessage}`);
            setActiveJob(null);
          }
        } catch (error) {
          console.error('Error polling job status:', error);
        }
      }, 2000); // Poll every 2 seconds

      setPollingInterval(interval);
    },
    [currentAccount.accountId, searchState],
  );

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Bulk operations
  const handleBulkMoveToTrash = useCallback(() => {
    const hasPermanentDocs =
      !selectAllResultsMode &&
      selectedDocuments.some((docId) => {
        const doc = documents?.find((d) => d.id === docId);
        return doc?.permanent;
      });

    setConfirmModalConfig({
      headerText: 'Move to Trash',
      bodyText: `Are you sure you want to move ${effectiveSelectedCount} document${effectiveSelectedCount !== 1 ? 's' : ''} to Trash?`,
      confirmButtonText: 'Move to Trash',
      confirmButtonColor: 'warning',
      warningText: hasPermanentDocs ? 'This action will NOT change documents marked as permanent to Trash.' : undefined,
      onConfirm: async () => {
        setConfirmModalOpen(false);
        try {
          toast.info(`Starting move to trash for ${effectiveSelectedCount.toLocaleString()} document${effectiveSelectedCount !== 1 ? 's' : ''}...`);

          let job;
          if (selectAllResultsMode) {
            // Use query-based approach with exclusions
            job = await createTrashJob(currentAccount.accountId, query, excludedDocumentIds, undefined);
          } else {
            // Use specific document IDs
            job = await createTrashJob(currentAccount.accountId, undefined, undefined, selectedDocuments);
          }

          startJobPolling(job.jobId, 'trash');
          setSelectedDocuments([]);
          setSelectAllResultsMode(false);
          setExcludedDocumentIds([]);
        } catch (error) {
          toast.error('Unable to start move to trash job');
        }
      },
    });
    setConfirmModalOpen(true);
  }, [selectAllResultsMode, selectedDocuments, documents, excludedDocumentIds, currentAccount.accountId, query, effectiveSelectedCount, startJobPolling]);

  const handleBulkMoveToPermanent = useCallback(() => {
    const isRestoringFromTrash = status === 'TRASH';
    const headerText = isRestoringFromTrash ? 'Move to Active' : 'Move to Permanent';
    const actionText = isRestoringFromTrash ? 'restore' : 'mark as Permanent';
    const updates = isRestoringFromTrash ? { status: 'ACTIVE' } : { permanent: true };

    setConfirmModalConfig({
      headerText,
      bodyText: `Are you sure you want to ${actionText} ${effectiveSelectedCount} document${effectiveSelectedCount !== 1 ? 's' : ''}?`,
      confirmButtonText: headerText,
      confirmButtonColor: 'primary',
      onConfirm: async () => {
        setConfirmModalOpen(false);
        try {
          const operationDescription = isRestoringFromTrash ? 'Restoring documents...' : 'Marking documents as Permanent...';
          const successMessage = isRestoringFromTrash ? 'restored to Active' : 'marked as Permanent';

          // Use async job for large operations
          if (effectiveSelectedCount >= ASYNC_JOB_THRESHOLD) {
            toast.info(`Starting bulk operation for ${effectiveSelectedCount.toLocaleString()} documents...`);
            const job = await createBulkOperationJob(currentAccount.accountId, query, excludedDocumentIds, updates);
            startJobPolling(job.jobId);
          } else if (selectAllResultsMode) {
            toast.info(operationDescription);
            const result = await bulkUpdateDocumentsByQuery(currentAccount.accountId, query, excludedDocumentIds, updates);
            toast.success(`${result.successful} document${result.successful !== 1 ? 's' : ''} ${successMessage}`);
            setSelectedDocuments([]);
            setSelectAllResultsMode(false);
            setExcludedDocumentIds([]);
            dispatch({ type: 'sticky_search', prevState: searchState });
          } else {
            toast.info(operationDescription);
            await bulkUpdateDocuments(currentAccount.accountId, selectedDocuments, updates);
            toast.success(`${selectedDocuments.length} document${selectedDocuments.length !== 1 ? 's' : ''} ${successMessage}`);
            setSelectedDocuments([]);
            setSelectAllResultsMode(false);
            setExcludedDocumentIds([]);
            dispatch({ type: 'sticky_search', prevState: searchState });
          }
        } catch (error) {
          const errorMessage = isRestoringFromTrash ? 'Unable to restore documents' : 'Unable to mark documents as Permanent';
          toast.error(errorMessage);
        }
      },
    });
    setConfirmModalOpen(true);
  }, [
    selectAllResultsMode,
    selectedDocuments,
    excludedDocumentIds,
    currentAccount.accountId,
    query,
    searchState,
    effectiveSelectedCount,
    startJobPolling,
    status,
  ]);

  const handleBulkEmptyTrash = useCallback(() => {
    setConfirmModalConfig({
      headerText: 'Empty Trash',
      bodyText: `Are you sure you wish to permanently delete these ${effectiveSelectedCount} document${effectiveSelectedCount !== 1 ? 's' : ''}?`,
      confirmButtonText: 'Delete',
      confirmButtonColor: 'danger',
      warningText: 'This action cannot be undone. These documents will be permanently deleted.',
      onConfirm: async () => {
        setConfirmModalOpen(false);
        try {
          toast.info(`Starting deletion of ${effectiveSelectedCount.toLocaleString()} document${effectiveSelectedCount !== 1 ? 's' : ''}...`);

          let job;
          if (selectAllResultsMode) {
            // Use query-based approach with exclusions
            job = await createPurgeJob(currentAccount.accountId, query, excludedDocumentIds, undefined);
          } else {
            // Use specific document IDs
            job = await createPurgeJob(currentAccount.accountId, undefined, undefined, selectedDocuments);
          }

          startJobPolling(job.jobId, 'purge');
          setSelectedDocuments([]);
          setSelectAllResultsMode(false);
          setExcludedDocumentIds([]);
        } catch (error) {
          toast.error('Unable to start deletion job');
        }
      },
    });
    setConfirmModalOpen(true);
  }, [selectAllResultsMode, selectedDocuments, excludedDocumentIds, currentAccount.accountId, query, effectiveSelectedCount, startJobPolling]);

  return (
    <>
      {documents ? (
        <DocumentList
          loadingDocuments={searching}
          totalCount={totalCount}
          updatePage={updatePage}
          startNewSearch={resetSearch}
          pageSize={pageSize}
          documents={documents}
          currentPage={currentPage}
          addResultsToCart={addResultsToCart}
          showInactiveTypes={showInactiveTypes}
          currentSort={sort}
          updateSort={updateSort}
          enableBulkSelection={true}
          selectDocument={selectDocument}
          selectedDocuments={selectedDocuments}
          onSelectAll={handleSelectAll}
          onSelectAllResults={handleSelectAllResults}
          selectAllResultsMode={selectAllResultsMode}
          excludedDocumentIds={excludedDocumentIds}
          effectiveSelectedCount={effectiveSelectedCount}
          activeJob={activeJob}
          onBulkMoveToTrash={handleBulkMoveToTrash}
          onBulkMoveToPermanent={handleBulkMoveToPermanent}
          onBulkEmptyTrash={handleBulkEmptyTrash}
          currentSearchStatus={status}
        />
      ) : (
        <SearchComponent
          submitSearch={submitSearch}
          searchError={error}
          searching={searching}
          showInactiveTypes={showInactiveTypes}
          setShowInactiveTypes={setShowInactiveTypes}
        />
      )}
      {confirmModalConfig && (
        <ConfirmBulkAction
          modal={confirmModalOpen}
          toggle={() => setConfirmModalOpen(false)}
          headerText={confirmModalConfig.headerText}
          bodyText={confirmModalConfig.bodyText}
          confirmButtonText={confirmModalConfig.confirmButtonText}
          confirmButtonColor={confirmModalConfig.confirmButtonColor}
          warningText={confirmModalConfig.warningText}
          onConfirm={confirmModalConfig.onConfirm}
        />
      )}
    </>
  );
};

export default DocumentSearch;
